import { motion } from "framer-motion";
import { ShieldCheck, Gauge, Users, Handshake } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import StackMarquee from "@/components/StackMarquee";
import ProcessSection from "@/components/ProcessSection";
import CTASection from "@/components/CTASection";
import { COMPANY } from "@/data/content";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Security is the default",
    copy: "Every pipeline we ship carries SAST, SBOMs and policy-as-code from day one — compliance is engineered in, not bolted on.",
  },
  {
    icon: Gauge,
    title: "Outcomes over hours",
    copy: "We commit to measurable results — deploy frequency, MTTR, cloud spend — and report against them every sprint.",
  },
  {
    icon: Users,
    title: "Senior engineers only",
    copy: "No bait-and-switch staffing. The engineers on the discovery call are the engineers in your repos.",
  },
  {
    icon: Handshake,
    title: "Leave-behind, not lock-in",
    copy: "Everything is documented, IaC-managed and handed over. Your team can run the platform without us — most keep us anyway.",
  },
];

export default function AboutPage() {
  return (
    <>
      <PageHeader
        eyebrow="/ About"
        title={<>Platform engineers, <span className="text-gradient">based in King of Prussia, PA.</span></>}
        copy={`${COMPANY.name} is a hands-on DevOps and DevSecOps consultancy. We design, migrate and operate production cloud platforms for teams that can't afford downtime — or surprises on the cloud bill.`}
      />

      <section className="relative py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
              className="glass rounded-2xl p-8"
              data-testid={`value-card-${i}`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-white/10">
                <v.icon size={22} className="text-cyan-300" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-white">{v.title}</h3>
              <p className="mt-3 text-slate-400 leading-relaxed">{v.copy}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <StackMarquee />
      <ProcessSection />
      <CTASection />
    </>
  );
}
